import { Button, Table, Tag } from "@caprikorn/dynamo-ui";
import type { FetchDataProps } from "@caprikorn/dynamo-ui/lib/components/dataDisplay/Table/Table.types";
import { type FC, useState } from "react";
import { useParams } from "react-router-dom";

import { useGroupReferences } from "@contexts/GroupReferences/GroupReferences.context";
import { useTenant } from "@contexts/Tenant/Tenant.context";
import { AddPlus, Users } from "@icons";
import type { UrlParam } from "@router/router.types";
import type { Site } from "@services/apiEcms/apiEcms.types";
import { useGetGroup } from "@services/apiEcms/useGetGroup";
import { useGetSites } from "@services/apiEcms/useGetSites";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import { usePerimeterSitesTableColumns } from "./PerimeterSitesTable.columns";
import classes from "./PerimeterSitesTable.module.css";
import { AddPodDrawer } from "../AddPodDrawer/AddPodDrawer";

const initialFilters: FetchDataProps = {
  pageIndex: 0,
  pageSize: 10,
  globalFilter: "",
  columnFilters: [],
  sorting: [],
};

const getRowId = (row: Site) => row.siteInformation.siteId;

export const PerimeterSitesTable: FC = () => {
  const { t } = useCustomTranslation("main");
  const { hasTicketing } = useTenant();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { currentGroup } = useGroupReferences();
  const { contractId = "", customerId = "" } = useParams<UrlParam>();
  const { data: groupData } = useGetGroup({
    groupId: currentGroup,
  });
  const [filters, setFilters] = useState<FetchDataProps>(initialFilters);
  const params = {
    customerId,
    contractId,
    pageNumber: filters.pageIndex + 1,
    elementsPerPage: filters.pageSize,
    q: filters.globalFilter,
  };
  const { data, isPending, isError } = useGetSites(params);
  const columns = usePerimeterSitesTableColumns();
  const canAddPod =
    hasTicketing &&
    (groupData?.isAdmin || (customerId !== "" && contractId !== ""));

  return (
    <>
      <Table
        columns={columns}
        data={data?.sites || []}
        data-testid="PerimeterSitesTableProvider-table"
        fetchData={setFilters}
        getRowId={getRowId}
        initialColumnFilters={initialFilters.columnFilters}
        initialPageIndex={initialFilters.pageIndex}
        initialPageSize={initialFilters.pageSize}
        isError={isError}
        isLoading={isPending}
        totalCount={data?.paging.totalElements || 0}
      >
        <Table.ActionBar>
          {data?.paging.totalElements ? (
            <Tag
              icon={Users}
              iconStyle="start"
              label={t("perimeter.sites.countLabel", {
                count: data?.paging.totalElements,
              })}
              variant="primary"
            />
          ) : null}
          <div className={classes.actions}>
            <Table.ActionBar.FilterBar
              hasGlobalFilter
              searchPlaceholder={t("perimeter.sites.searchPlaceholder")}
            />
            {canAddPod && (
              <Button
                data-testid="AddPodDrawer-open"
                onClick={() => setIsDrawerOpen(true)}
                size="sm"
                startIcon={AddPlus}
                type="button"
                variant="secondary"
              >
                {t("perimeter.sites.addPod")}
              </Button>
            )}
          </div>
        </Table.ActionBar>
        <Table.Content />
        <Table.Pagination withPageSizeSelector />
      </Table>

      {isDrawerOpen && (
        <AddPodDrawer
          isShown={isDrawerOpen}
          onHide={() => setIsDrawerOpen(false)}
        />
      )}
    </>
  );
};
